// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.SecurityQuestions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import constants from 'logic/data/Izenda.SecurityPoliciesConstant';
import { toNumber } from 'lodash/fp';
import EventsUtils from 'EventUtils';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
import action from 'modules/Settings/SystemConfiguration/SecurityPolicies/Izenda.SecurityPoliciesDetailAction';
const ValidatableInput = Validator()(Input);
const minField = 'numberOfQuestionResetPassword';
const maxField = 'numberOfQuestionProfile';
const notifyChange = EventsUtils.notifyChange;

const SecurityQuestions = connect(action)(function(props: any) {
  const localization = props.localization;
  return (
    <GridLayout>
      <GridLayout.Row className='u_noMarginAll u_borderBottom izenda-ItemContentHeight'>
        <GridLayout.Col cols='12' className='u_noPaddingAll'>
          <span className='u_textBold u_marginLeft'>
            {localization.getText('SYSTEM_CONFIGURATION_SECURITY_POLICIES_LABEL_SECURITY_QUESTION')}
          </span>
        </GridLayout.Col>
      </GridLayout.Row>

      <SecurityQuestionsMessage {...props} />
      <UseSecurityQuestions {...props} />
      <SecurityQuestionsUserProfile {...props} />
      <SecurityQuestionsResetPassword {...props} />
    </GridLayout>
  );
});

const SecurityQuestionsMessage = connect(action)(function(props: any) {
  return (
    <GridLayout.Row className='u_noMarginAll u_borderBottom izenda-ItemContentHeight'>
      <GridLayout.Col cols='12' className='u_noPaddingAll'>
        <i className='icon-info-circled u_marginLeftRight' />
        <span>{constants.SECURITY_QUESTIONS}</span>
      </GridLayout.Col>
    </GridLayout.Row>
  );
});

const UseSecurityQuestions = connect(action)(function(props: any) {
  const localization = props.localization;
  const model = props.store.model;
  const field = 'useSecurityQuestions';
  return (
    <GridLayout.Row className='u_noMarginAll u_borderBottom izenda-ItemContentHeight'>
      <GridLayout.Col cols='12' className='u_noPaddingAll'>
        <GridLayout.Col cols='4'>
          <label>
            <Input
              type='checkbox'
              className='u_marginLeft'
              checked={model[field]}
              onChange={notifyChange(props.onChangeValueCheckbox.bind(props, field), EventsUtils.CHECKBOX)}
            />
            <span className='u_marginLeft'>
              {localization.getText('SYSTEM_CONFIGURATION_SECURITY_POLICIES_LABEL_USE_SECURITY_QUESTION')}
            </span>
          </label>
        </GridLayout.Col>
        <GridLayout.Col cols='8' />
      </GridLayout.Col>
    </GridLayout.Row>
  );
});

const SecurityQuestionsUserProfile = connect(action)(function(props: any) {
  const model = props.store.model;
  const validations = function(isMinField) {
    return constants.securityQuestionConstraint(
      isMinField,
      constants.SECURITY_QUESTION_FIELD_NAME.RESET_PASSWORD,
      constants.SECURITY_QUESTION_FIELD_NAME.USER_PROFILE,
      model[minField],
      model[maxField]
    );
  };

  return (
    <GridLayout.Row className='u_noMarginAll u_borderBottom izenda-ItemContentHeight'>
      <GridLayout.Col cols='12' className='u_noPaddingAll'>
        <GridLayout.Col cols='4'>
          <span className='izenda-CustomInput-Position'>{constants.SECURITY_QUESTION_FIELD_NAME.USER_PROFILE}</span>
        </GridLayout.Col>
        <GridLayout.Col cols='8'>
          <ValidatableInput
            type='input'
            containerClassName='u_inline'
            className='izenda-CustomInput-Position form-control u_inlineBlock'
            disabled={!model.useSecurityQuestions}
            onBlur={props.onBlurFocusField.bind(props, [minField, maxField])}
            field={maxField}
            value={model[maxField]}
            onChange={notifyChange(
              props.onChangeRequiredValue.bind(props, maxField, constants.REQUIRED_VALUE_NO_SECURITY_QUESTIONS),
              EventsUtils.INPUT
            )}
            validations={validations(false)}
          />
        </GridLayout.Col>
      </GridLayout.Col>
    </GridLayout.Row>
  );
});

const SecurityQuestionsResetPassword = connect(action)(function(props: any) {
  const model = props.store.model;
  const validations = function(isMinField) {
    return constants.securityQuestionConstraint(
      isMinField,
      constants.SECURITY_QUESTION_FIELD_NAME.RESET_PASSWORD,
      constants.SECURITY_QUESTION_FIELD_NAME.USER_PROFILE,
      model[minField],
      model[maxField]
    );
  };
  const enable = model.useSecurityQuestions && toNumber(model[maxField]) !== 0;

  return (
    <GridLayout.Row className='u_noMarginAll u_borderBottom izenda-ItemContentHeight'>
      <GridLayout.Col cols='12' className='u_noPaddingAll'>
        <GridLayout.Col cols='4'>
          <span className='izenda-CustomInput-Position'>{constants.SECURITY_QUESTION_FIELD_NAME.RESET_PASSWORD}</span>
        </GridLayout.Col>
        <GridLayout.Col cols='8'>
          <ValidatableInput
            type='input'
            containerClassName='u_inline'
            className='izenda-CustomInput-Position form-control u_inlineBlock'
            onBlur={props.onBlurFocusField.bind(props, [minField, maxField])}
            field={minField}
            disabled={!enable}
            value={model[minField]}
            onChange={notifyChange(props.onChangeRequiredPositiveValue.bind(props, minField), EventsUtils.INPUT)}
            validations={validations(true)}
          />
        </GridLayout.Col>
      </GridLayout.Col>
    </GridLayout.Row>
  );
});

export default SecurityQuestions;



// WEBPACK FOOTER //
// ./ui/modules/Settings/SystemConfiguration/SecurityPolicies/PasswordPolicies/Izenda.SecurityQuestions.js